import React from "react";
import Card from "../ui/Card";

const CommissionBreakdown = ({
  setIsCommissionBreakdownExpanded,
  isCommissionBreakdownExpanded,
  affiliateStats,
  affiliateData,
}) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  // Helpers to support different data shapes
  const getSpend = (ad) => parseFloat(ad["Ad Spend"] ?? ad.ad_spend ?? 0);
  const getCommission = (ad) =>
    parseFloat(ad["Commission"] ?? ad.estimated_commission ?? 0);
  const getROAS = (ad) => parseFloat(ad.ROAS ?? ad.roas ?? 0);
  const getConversions = (ad) =>
    parseFloat(ad["Pixel Purchases"] ?? ad.total_conversions ?? 0);
  const getAdName = (ad) => ad["Ad Name"] ?? ad.ad_name ?? "Unnamed Ad";

  // Group ads by performance tiers
  const performanceTiers = [
    {
      name: "Top Performers",
      ads: affiliateData.filter((ad) => getROAS(ad) >= 3.0),
      color: "from-yellow-500/20 to-orange-500/20",
      borderColor: "border-yellow-500/30",
      textColor: "text-yellow-400",
      iconColor: "text-yellow-400",
      bgColor: "bg-yellow-500/10",
    },
    {
      name: "High Performers",
      ads: affiliateData.filter(
        (ad) => getROAS(ad) >= 2.0 && getROAS(ad) < 3.0
      ),
      color: "from-green-500/20 to-emerald-500/20",
      borderColor: "border-green-500/30",
      textColor: "text-green-400",
      iconColor: "text-green-400",
      bgColor: "bg-green-500/10",
    },
    {
      name: "Medium Performers",
      ads: affiliateData.filter(
        (ad) => getROAS(ad) >= 1.5 && getROAS(ad) < 2.0
      ),
      color: "from-blue-500/20 to-cyan-500/20",
      borderColor: "border-blue-500/30",
      textColor: "text-blue-400",
      iconColor: "text-blue-400",
      bgColor: "bg-blue-500/10",
    },
    {
      name: "Low Performers",
      ads: affiliateData.filter((ad) => getROAS(ad) < 1.5),
      color: "from-red-500/20 to-pink-500/20",
      borderColor: "border-red-500/30",
      textColor: "text-red-400",
      iconColor: "text-red-400",
      bgColor: "bg-red-500/10",
    },
  ];

  const getPerformanceIcon = (tierName) => {
    switch (tierName) {
      case "Top Performers":
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      case "High Performers":
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
          </svg>
        );
      case "Medium Performers":
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92z" />
          </svg>
        );
    }
  };

  return (
    <div className="mb-6">
      <Card className="bg-white/5 backdrop-blur-2xl border border-white/10 shadow-xl relative overflow-hidden">
        {/* Shine effect */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/3 to-transparent rounded-2xl pointer-events-none"
          style={{ animation: `shimmer 6s ease-in-out infinite` }}
        ></div>

        {/* Header */}
        <div className="p-6 border-b border-white/10 relative z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              Commission Breakdown
            </h2>
            <button
              onClick={() =>
                setIsCommissionBreakdownExpanded(!isCommissionBreakdownExpanded)
              }
              className="bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 hover:border-white/30 text-white font-medium px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 text-sm relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none rounded-xl"></div>
              <span className="relative z-10">
                {isCommissionBreakdownExpanded
                  ? "Hide Details"
                  : "Show Details"}
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        {isCommissionBreakdownExpanded && (
          <div className="p-6 relative z-10">
            {/* Performance Tiers Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {performanceTiers.map((tier, index) => (
                <Card
                  key={tier.name}
                  className={`p-6 bg-gradient-to-br ${tier.color} backdrop-blur-2xl border ${tier.borderColor} shadow-xl relative overflow-hidden group`}
                >
                  {/* Shine effect with staggered timing */}
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent rounded-2xl pointer-events-none"
                    style={{
                      animation: `shimmer 6s ease-in-out infinite ${index * 0.8}s`,
                    }}
                  ></div>

                  <div className="relative z-10">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-10 h-10 ${tier.bgColor} backdrop-blur-xl border ${tier.borderColor} rounded-xl flex items-center justify-center`}
                      >
                        <div className={tier.iconColor}>
                          {getPerformanceIcon(tier.name)}
                        </div>
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${tier.textColor}`}>
                          {tier.name}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {tier.ads.length} ads
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                          Total Spend
                        </div>
                        <div className="text-white font-bold text-lg">
                          {formatCurrency(
                            tier.ads.reduce((sum, ad) => sum + getSpend(ad), 0)
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                          Commission
                        </div>
                        <div className={`font-bold text-lg ${tier.textColor}`}>
                          {formatCurrency(
                            tier.ads.reduce(
                              (sum, ad) => sum + getCommission(ad),
                              0
                            )
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                          Avg ROAS
                        </div>
                        <div className="text-white font-bold text-base">
                          {tier.ads.length > 0
                            ? (
                                tier.ads.reduce(
                                  (sum, ad) => sum + getROAS(ad),
                                  0
                                ) / tier.ads.length
                              ).toFixed(2)
                            : "0.00"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                          Conversions
                        </div>
                        <div className="text-white font-bold text-base">
                          {formatNumber(
                            tier.ads.reduce(
                              (sum, ad) => sum + getConversions(ad),
                              0
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Top performing ads in this tier */}
                    {tier.ads.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="text-gray-400 text-xs uppercase tracking-wide mb-2">
                          Top Ad
                        </div>
                        <div className="text-white text-sm font-medium truncate">
                          {getAdName(
                            tier.ads.sort(
                              (a, b) => getCommission(b) - getCommission(a)
                            )[0]
                          )}
                        </div>
                        <div className={`text-xs ${tier.textColor}`}>
                          {formatCurrency(
                            getCommission(
                              tier.ads.sort(
                                (a, b) => getCommission(b) - getCommission(a)
                              )[0]
                            )
                          )}{" "}
                          commission
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Summary Stats */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <h3 className="text-lg font-bold text-white mb-4">
                Overall Summary
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">
                    {formatNumber(affiliateData.length)}
                  </div>
                  <div className="text-gray-400 text-sm">Total Ads</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">
                    {formatCurrency(affiliateStats.totalAdSpend)}
                  </div>
                  <div className="text-gray-400 text-sm">Total Spend</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">
                    {affiliateStats.avgROAS.toFixed(2)}
                  </div>
                  <div className="text-gray-400 text-sm">Average ROAS</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#B8460E]">
                    {formatCurrency(affiliateStats.totalCommission)}
                  </div>
                  <div className="text-gray-400 text-sm">Total Commission</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CommissionBreakdown;
